'use client';

import { featureCollection, type Point, point } from '@turf/helpers';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { COLOR_TEXT_PRIMARY } from '@alltrails/denali/tokens';
import useMap from '@alltrails/maps/hooks/useMap';
import { curatedWaypointsId, defaultSymbolLayerLayoutProps, getLayerBeforeId } from '@alltrails/maps/utils/layers';
import curatedWaypointSvg from '@alltrails/maps/utils/svgDefinitions/curatedWaypointSvg';
import useSvgImages, { type SvgDefinition } from '@alltrails/maps/utils/useSvgImages';
import type { PrintRouteWaypoint } from '../../types/PrintRouteWaypoint';

const svgDefinitions: SvgDefinition[] = [curatedWaypointSvg];

type PrintCuratedWaypointPinsProps = { waypoints: PrintRouteWaypoint[] };

const PrintCuratedWaypointPins = ({ waypoints }: PrintCuratedWaypointPinsProps) => {
  const map = useMap();
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const data = useMemo(
    () => featureCollection<Point>(waypoints.map(({ latitude, longitude }, index) => point([longitude, latitude], { order: index + 1 }))),
    [waypoints]
  );

  if (!imagesAreLoaded || data.features.length === 0) {
    return null;
  }

  return (
    <Source id="print-curated-waypoints" type="geojson" data={data}>
      <Layer
        beforeId={getLayerBeforeId(map, curatedWaypointsId)}
        id={curatedWaypointsId}
        layout={{
          ...defaultSymbolLayerLayoutProps,
          'icon-image': curatedWaypointSvg.name,
          'text-anchor': 'center',
          'text-field': ['get', 'order']
        }}
        paint={{ 'text-color': COLOR_TEXT_PRIMARY }}
        source="print-curated-waypoints"
        type="symbol"
      />
    </Source>
  );
};

export default PrintCuratedWaypointPins;
